import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { Home, Scissors, Brain, ShoppingBag, type LucideIcon } from "lucide-react"

type NavKey = string

interface NavItem {
  key: NavKey
  name: string
  icon: LucideIcon
}

interface NavBarProps {
  items?: NavItem[]
  className?: string
  value?: NavKey
  onChange?: (key: NavKey) => void
}

// Default items for Headman
const defaultItems: NavItem[] = [
  { key: "home", name: "Главная", icon: Home },
  { key: "hairstyle", name: "Студия", icon: Scissors },
  { key: "quiz", name: "Подбор", icon: Brain },
  { key: "shop", name: "Магазин", icon: ShoppingBag },
]

export function TubelightNavbar({ 
  items = defaultItems, 
  className = "", 
  value, 
  onChange 
}: NavBarProps) {
  const [activeTab, setActiveTab] = useState<NavKey>(value ?? items[0]?.key)

  useEffect(() => {
    if (value !== undefined) {
      setActiveTab(value)
    }
  }, [value])

  const handleClick = (item: NavItem) => {
    setActiveTab(item.key)
    onChange?.(item.key)
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 px-4 pb-safe pt-2 ${className}`}>
      <div className="mx-auto max-w-md w-full">
        <div 
          className="flex items-center justify-between gap-1 py-2 px-2 rounded-full border border-white/10 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]"
          style={{
            background: 'rgba(10, 10, 10, 0.95)',
            backdropFilter: 'blur(20px)',
          }}
        >
          {items.map((item) => {
            const Icon = item.icon
            const isActive = activeTab === item.key

            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleClick(item)}
                className="relative flex-1 flex flex-col items-center gap-1 py-2 px-3 rounded-full transition-colors"
              >
                {/* Tubelight glow effect */}
                {isActive && (
                  <motion.div
                    layoutId="tubelight"
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: 'rgba(255, 255, 255, 0.08)',
                    }}
                    initial={false}
                    transition={{
                      type: "spring",
                      stiffness: 380,
                      damping: 28,
                    }}
                  >
                    {/* Top indicator bar - iOS system style, thicker for confidence */}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-10 h-[3.5px] rounded-full bg-[rgba(255,255,255,0.9)]">
                      <div className="absolute inset-0 rounded-full blur-[2.5px] bg-[rgba(255,255,255,0.6)]" />
                    </div>
                  </motion.div>
                )}

                <motion.div
                  animate={{
                    scale: isActive ? 1.1 : 1,
                    color: isActive ? 'rgba(255, 255, 255, 0.95)' : '#666666',
                  }}
                  transition={{ duration: 0.2 }}
                  className="relative z-10"
                  style={{
                    filter: isActive ? 'drop-shadow(0 1px 2px rgba(255, 255, 255, 0.2))' : 'none',
                  }}
                >
                  <Icon size={20} strokeWidth={2} />
                </motion.div>
                
                <motion.span
                  animate={{
                    color: isActive ? 'rgba(255, 255, 255, 0.95)' : '#666666',
                  }}
                  className="relative z-10 text-[10px] font-medium"
                >
                  {item.name}
                </motion.span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

